/**
 * Retour de validation : une micro-animation, plus une vibration là où la
 * plateforme en fournit une (specs §8.1).
 *
 * Pourquoi un hook plutôt qu'un appel direct dans chaque `onSuccess` : la
 * classe doit être **retirée** pour pouvoir être remise au geste suivant. Sans
 * cela, valider deux fois d'affilée ne joue l'animation qu'une fois — le
 * navigateur ne rejoue pas une animation dont la classe n'a jamais quitté
 * l'élément, et c'est précisément le cas d'usage dimensionnant (plusieurs
 * ajouts de suite dans un repas).
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { confirmTap } from '../lib/haptics';

/** Durée de `confirm-pop` dans `surfaces.css`. Les deux doivent rester d'accord. */
const CONFIRM_MS = 220;

export function useConfirm(): { confirming: boolean; confirm: () => void } {
  const [confirming, setConfirming] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  const confirm = useCallback(() => {
    // La vibration part d'abord : elle est instantanée là où elle existe, et
    // sur l'appareil cible elle ne fait rien (cf. `lib/haptics.ts`).
    confirmTap();

    if (timer.current !== null) window.clearTimeout(timer.current);
    // Retirer puis reposer la classe à la frame suivante : c'est ce qui permet
    // de rejouer l'animation sur deux validations rapprochées.
    setConfirming(false);
    requestAnimationFrame(() => {
      setConfirming(true);
      timer.current = window.setTimeout(() => {
        setConfirming(false);
        timer.current = null;
      }, CONFIRM_MS);
    });
  }, []);

  return { confirming, confirm };
}
